import { useState, useMemo, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { GoogleAdsAccount } from '@/lib/types';
import { GoogleAdsAccountItem } from './googleads-account-item';
import { motion } from 'framer-motion';

interface GoogleAdsAccountListProps {
  accounts: GoogleAdsAccount[];
  selectedAccountId?: string;
  onSelect: (account: GoogleAdsAccount) => Promise<void> | void;
  excludeAccountId?: string;
  showSearchThreshold?: number;
  emptyMessage?: string;
  emptySearchMessage?: string;
  renderEmptyState?: (searchQuery: string, hasAccounts: boolean) => React.ReactNode;
  animated?: boolean;
  className?: string;
  listClassName?: string;
}

export function GoogleAdsAccountList({
  accounts,
  selectedAccountId,
  onSelect,
  excludeAccountId,
  showSearchThreshold = 5,
  emptyMessage = 'No Google Ads accounts found',
  emptySearchMessage,
  renderEmptyState,
  animated = false,
  className = '',
  listClassName = '',
}: GoogleAdsAccountListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const selectedItemRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  // Auto-scroll to selected item on initial mount
  useEffect(() => {
    if (selectedAccountId && selectedItemRef.current && !hasScrolledRef.current) {
      // Small delay to ensure the container is rendered
      const timer = setTimeout(() => {
        selectedItemRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        hasScrolledRef.current = true;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedAccountId]);

  const filteredAccounts = useMemo(() => {
    let filtered = accounts;

    // Exclude specific account if provided (e.g., for transfer mode)
    if (excludeAccountId) {
      filtered = filtered.filter(
        (account) => account.googleads_account_id !== excludeAccountId
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (account) =>
          account.name.toLowerCase().includes(query) ||
          account.googleads_account_id.includes(query)
      );
    }

    return filtered;
  }, [accounts, searchQuery, excludeAccountId]);

  const showSearchBar = accounts.length > showSearchThreshold;

  const handleSelect = async (account: GoogleAdsAccount) => {
    await onSelect(account);
  };

  const renderAccountItem = (account: GoogleAdsAccount, index: number) => {
    const isSelected = selectedAccountId === account.googleads_account_id;
    const item = (
      <GoogleAdsAccountItem
        ref={isSelected ? selectedItemRef : undefined}
        selected={isSelected}
        account={account}
        onSelect={handleSelect}
      />
    );

    if (animated) {
      return (
        <motion.div
          key={`google-account-id-${account.googleads_account_id}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
        >
          {item}
        </motion.div>
      );
    }

    return (
      <div key={`google-account-id-${account.googleads_account_id}`}>
        {item}
      </div>
    );
  };

  const renderDefaultEmptyState = () => {
    // Use custom render function if provided
    if (renderEmptyState) {
      return renderEmptyState(searchQuery, accounts.length > 0);
    }

    if (searchQuery.trim() && accounts.length > 0) {
      // Filtered results are empty but accounts exist
      return (
        <div className="text-center py-8 text-muted-foreground">
          <p>{emptySearchMessage || `No accounts found matching "${searchQuery}"`}</p>
        </div>
      );
    }

    // No accounts at all
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>{emptyMessage}</p>
      </div>
    );
  };

  return (
    <div className={className}>
      {showSearchBar && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search accounts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      )}
      <div className={listClassName}>
        {/* Rows own selection via onClick (Meta-picker parity, #5936) — no RadioGroup needed. */}
        <div className="space-y-1">
          {filteredAccounts.length > 0
            ? filteredAccounts.map((account, index) =>
                renderAccountItem(account, index)
              )
            : renderDefaultEmptyState()}
        </div>
      </div>
    </div>
  );
}
